import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      for (let i = 0; i < 10; i++) {
        if (cancelled) return;
        try {
          const { data } = await api.get(`/payments/status/${sessionId}`);
          setAttempts(i + 1);
          if (data.payment_status === "paid") { setStatus("paid"); return; }
          if (data.payment_status === "failed" || data.payment_status === "expired") { setStatus("failed"); return; }
        } catch (err) { console.warn("Payment status check failed, retrying", err); }
        await new Promise((r) => setTimeout(r, 2000));
      }
      setStatus("timeout");
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen grid place-items-center bg-[#0A0D14] text-slate-100 px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full rounded-3xl border border-white/5 bg-white/[0.02] p-8 text-center">
        {status === "paid" ? (
          <>
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-400 mb-4" />
            <h1 className="text-2xl font-bold">You&apos;re in.</h1>
            <p className="mt-2 text-slate-400 text-sm">Subscription active. Your AI BDR agents are warming up.</p>
            <Button data-testid="payment-success-cta" onClick={() => navigate("/dashboard")} className="mt-6 bg-sky-500 hover:bg-sky-400 text-slate-950 font-semibold rounded-full">Go to dashboard</Button>
          </>
        ) : status === "failed" || status === "error" || status === "timeout" ? (
          <>
            <XCircle className="w-14 h-14 mx-auto text-rose-400 mb-4" />
            <h1 className="text-2xl font-bold">{status === "timeout" ? "Still processing" : "Payment issue"}</h1>
            <p className="mt-2 text-slate-400 text-sm">
              {status === "error" && "Missing or invalid session. Head back to pricing to try again."}
              {status === "failed" && "No charge was made. Try again from pricing."}
              {status === "timeout" && "This is taking longer than expected. Your account will update as soon as Stripe confirms."}
            </p>
            <Button onClick={() => navigate("/")} className="mt-6 bg-white text-slate-900 rounded-full" data-testid="payment-success-back">Back to pricing</Button>
          </>
        ) : (
          <>
            <div className="w-10 h-10 border-2 border-sky-400 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <div className="text-sm text-slate-400">Confirming payment… ({attempts}/10)</div>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen grid place-items-center bg-[#0A0D14] text-slate-100 px-6" data-testid="payment-cancel-page">
      <div className="max-w-md w-full rounded-3xl border border-white/5 bg-white/[0.02] p-8 text-center">
        <XCircle className="w-14 h-14 mx-auto text-slate-500 mb-4" />
        <h1 className="text-2xl font-bold">Checkout cancelled</h1>
        <p className="mt-2 text-slate-400 text-sm">No worries — you can try again anytime.</p>
        <Button onClick={() => navigate("/")} className="mt-6 bg-white text-slate-900 rounded-full" data-testid="payment-cancel-cta">Back to pricing</Button>
      </div>
    </div>
  );
}
